// src/engine/MoveOrdering.ts
// Enhanced move ordering with History Heuristic and Counter-Moves
// Expected improvement: ~30-50 Elo

import { PrioritizedMove } from "./MoveGenerator.ts";
import { PieceColor } from "../types/Board.ts";

const MAX_DEPTH = 100;

/**
 * Enhanced Move Ordering System
 * 
 * Combines multiple heuristics to order moves effectively:
 * 1. TT move (handled externally)
 * 2. MVV-LVA for captures (handled in MoveGenerator)
 * 3. Killer moves (2 per ply)
 * 4. Counter-moves (refutations to opponent's last move)
 * 5. History heuristic (historically good quiet moves)
 * 
 * Expected gain: ~30-50 Elo
 */
export class MoveOrdering {
  // Killer moves: [ply][slot] - typically 2 slots per ply
  private killerMoves: (PrioritizedMove | null)[][];
  
  // Counter-moves: Map from "from-to" key to refutation move
  private counterMoves: Map<string, PrioritizedMove>;
  
  // History table: [colorIndex][from][to] -> score
  // colorIndex: 0 = white, 1 = black
  private historyTable: number[][][];
  
  private boardSize: number;

  constructor(boardSize: number = 64) {
    this.boardSize = boardSize;
    
    // Initialize killer moves (2 slots per ply)
    this.killerMoves = [];
    for (let ply = 0; ply < MAX_DEPTH; ply++) {
      this.killerMoves[ply] = [null, null];
    }
    
    // Initialize counter-moves
    this.counterMoves = new Map();
    
    // Initialize history table
    this.historyTable = this.createHistoryTable();
  }

  /**
   * Create history table structure
   */
  private createHistoryTable(): number[][][] {
    const table: number[][][] = [];
    
    // 2 colors
    for (let color = 0; color < 2; color++) {
      table[color] = [];
      
      // boardSize squares (from)
      for (let from = 0; from < this.boardSize; from++) {
        table[color][from] = [];
        
        // boardSize squares (to)
        for (let to = 0; to < this.boardSize; to++) {
          table[color][from][to] = 0;
        }
      }
    }
    
    return table;
  }

  /**
   * Get color index for history table
   */
  private getColorIndex(color: PieceColor): number {
    return color === 'white' ? 0 : 1;
  }

  /**
   * Convert position to square index
   */
  private positionToSquare(row: number, col: number, boardWidth: number): number {
    return row * boardWidth + col;
  }

  /**
   * Create move key for counter-move storage
   */
  private getMoveKey(move: PrioritizedMove): string {
    return `${move.from.row},${move.from.col}-${move.to.row},${move.to.col}`;
  }

  /**
   * Check if two moves are equal
   */
  private movesEqual(m1: PrioritizedMove, m2: PrioritizedMove): boolean {
    return (
      m1.from.row === m2.from.row &&
      m1.from.col === m2.from.col &&
      m1.to.row === m2.to.row &&
      m1.to.col === m2.to.col
    );
  }

  /**
   * Add a killer move at the given ply
   * Only non-capture moves should be killers
   */
  addKiller(move: PrioritizedMove, ply: number): void {
    if (ply < 0 || ply >= MAX_DEPTH) return;
    if (move.capturedPiece) return; // Only quiet moves
    
    const killers = this.killerMoves[ply];
    
    // Already the primary killer?
    if (killers[0] && this.movesEqual(killers[0], move)) {
      return;
    }
    
    // Shift: primary -> secondary, new move -> primary
    killers[1] = killers[0];
    killers[0] = move;
  }

  /**
   * Get killer move score
   */
  getKillerScore(move: PrioritizedMove, ply: number): number {
    if (ply < 0 || ply >= MAX_DEPTH) return 0;
    if (move.capturedPiece) return 0; // Only for quiet moves
    
    const killers = this.killerMoves[ply];
    
    if (killers[0] && this.movesEqual(killers[0], move)) {
      return 8000; // Primary killer
    }
    if (killers[1] && this.movesEqual(killers[1], move)) {
      return 7000; // Secondary killer
    }
    
    return 0;
  }

  /**
   * Get killer moves for a ply
   */
  getKillers(ply: number): (PrioritizedMove | null)[] {
    if (ply < 0 || ply >= MAX_DEPTH) return [null, null];
    return this.killerMoves[ply];
  }

  /**
   * Clear killer moves (call at start of new search)
   */
  clearKillers(): void {
    for (let ply = 0; ply < MAX_DEPTH; ply++) {
      this.killerMoves[ply] = [null, null];
    }
  }

  /**
   * Add a counter-move (refutation to opponent's last move)
   */
  addCounterMove(previousMove: PrioritizedMove | null, refutation: PrioritizedMove): void {
    if (!previousMove || refutation.capturedPiece) return;
    
    const key = this.getMoveKey(previousMove);
    this.counterMoves.set(key, refutation);
  }

  /**
   * Get counter-move score
   */
  getCounterMoveScore(move: PrioritizedMove, previousMove: PrioritizedMove | null): number {
    if (!previousMove || move.capturedPiece) return 0;
    
    const key = this.getMoveKey(previousMove);
    const counter = this.counterMoves.get(key);
    
    if (counter && this.movesEqual(move, counter)) {
      return 7500; // Between killers and history
    }
    
    return 0;
  }

  /**
   * Update history table when a move causes a cutoff
   * Bonus increases with depth (deeper = more important)
   */
  updateHistory(move: PrioritizedMove, color: PieceColor, depth: number, boardWidth: number): void {
    if (move.capturedPiece) return; // Only for quiet moves
    
    const colorIndex = this.getColorIndex(color);
    const fromSquare = this.positionToSquare(move.from.row, move.from.col, boardWidth);
    const toSquare = this.positionToSquare(move.to.row, move.to.col, boardWidth);
    
    if (fromSquare >= this.boardSize || toSquare >= this.boardSize) return;
    
    // Bonus increases with depth squared
    const bonus = depth * depth;
    
    this.historyTable[colorIndex][fromSquare][toSquare] += bonus;
    
    // Cap to prevent overflow
    if (this.historyTable[colorIndex][fromSquare][toSquare] > 10000) {
      this.historyTable[colorIndex][fromSquare][toSquare] = 10000;
    }
  }

  /**
   * Penalize moves that failed to cause cutoffs
   */
  penalizeHistory(move: PrioritizedMove, color: PieceColor, depth: number, boardWidth: number): void {
    if (move.capturedPiece) return;
    
    const colorIndex = this.getColorIndex(color);
    const fromSquare = this.positionToSquare(move.from.row, move.from.col, boardWidth);
    const toSquare = this.positionToSquare(move.to.row, move.to.col, boardWidth);
    
    if (fromSquare >= this.boardSize || toSquare >= this.boardSize) return;
    
    const penalty = -(depth * depth);
    
    this.historyTable[colorIndex][fromSquare][toSquare] += penalty;
    
    // Prevent going too negative
    if (this.historyTable[colorIndex][fromSquare][toSquare] < -10000) {
      this.historyTable[colorIndex][fromSquare][toSquare] = -10000;
    }
  }

  /**
   * Get history score for a move
   */
  getHistoryScore(move: PrioritizedMove, color: PieceColor, boardWidth: number): number {
    if (move.capturedPiece) return 0;
    
    const colorIndex = this.getColorIndex(color);
    const fromSquare = this.positionToSquare(move.from.row, move.from.col, boardWidth);
    const toSquare = this.positionToSquare(move.to.row, move.to.col, boardWidth);
    
    if (fromSquare >= this.boardSize || toSquare >= this.boardSize) return 0;
    
    const score = this.historyTable[colorIndex][fromSquare][toSquare];
    
    // Scale to avoid overwhelming other heuristics
    // Max score: ~10000 * 10 = 100,000
    return score * 10;
  }

  /**
   * Age the history table (reduce all values)
   * Call this periodically to prevent stale data
   */
  ageHistory(): void {
    for (let color = 0; color < 2; color++) {
      for (let from = 0; from < this.boardSize; from++) {
        for (let to = 0; to < this.boardSize; to++) {
          this.historyTable[color][from][to] = Math.floor(
            this.historyTable[color][from][to] / 2
          );
        }
      }
    }
  }

  /**
   * Clear history table (call at start of new game)
   */
  clearHistory(): void {
    this.historyTable = this.createHistoryTable();
  }

  /**
   * Clear counter-moves
   */
  clearCounterMoves(): void {
    this.counterMoves.clear();
  }

  /**
   * Clear all move ordering data
   */
  clearAll(): void {
    this.clearKillers();
    this.clearHistory();
    this.clearCounterMoves();
  }

  /**
   * Get statistics
   */
  getStats(): {
    killerCount: number;
    counterMoveCount: number;
    historyEntries: number;
  } {
    let killerCount = 0;
    for (let ply = 0; ply < MAX_DEPTH; ply++) {
      if (this.killerMoves[ply][0]) killerCount++;
      if (this.killerMoves[ply][1]) killerCount++;
    }
    
    let historyEntries = 0;
    for (let color = 0; color < 2; color++) {
      for (let from = 0; from < this.boardSize; from++) {
        for (let to = 0; to < this.boardSize; to++) {
          if (this.historyTable[color][from][to] !== 0) {
            historyEntries++;
          }
        }
      }
    }
    
    return {
      killerCount,
      counterMoveCount: this.counterMoves.size,
      historyEntries
    };
  }
}
